/**
 * Schemes barred from an operator-authored URL.
 *
 * Shared by two features that both put an address the operator typed into a
 * real `href`: page-builder documents and the navigation menu builder. ONE
 * list, for the same reason `UNSAFE_CSS_PATTERNS` is one list — a second copy
 * is a second thing to remember when a scheme is added, and the copy that gets
 * forgotten is the vulnerability.
 *
 * `data:` is denied alongside the two script schemes: top-level navigation to
 * `data:` is blocked by browsers today, but it costs nothing to refuse and a
 * menu link has no legitimate use for it.
 *
 * Note what is NOT here. `mailto:`, `tel:` and `#anchor` are ordinary
 * navigation targets — this is a deny list precisely so those keep working
 * without anyone maintaining an allow list of every scheme a real site uses.
 */
const DENIED_URL_SCHEMES = /^\s*(?:javascript|vbscript|data)\s*:/i;

/**
 * Browsers strip ASCII tab/newline/CR from a URL anywhere (not just around the
 * scheme) and trim leading C0 controls before parsing it, so `java\tscript:`
 * and `\x01javascript:` both resolve to a plain `javascript:` URL at click
 * time. Stripping every C0 control anywhere is a safe superset: it can only
 * make a check catch MORE bypass attempts, never fewer.
 */
export const stripControlCharacters = (value: string): string =>
  // eslint-disable-next-line no-control-regex -- stripping C0 controls is the point
  value.replace(/[\x00-\x1f]/g, "");

/**
 * Whether `url` reaches the browser as one of the denied schemes.
 *
 * Checked against the control-stripped string rather than the raw one, because
 * what matters is the URL the browser resolves at click time, not the bytes as
 * they were stored.
 */
export const hasDeniedUrlScheme = (url: string): boolean =>
  DENIED_URL_SCHEMES.test(stripControlCharacters(url));
